const KLAVIYO_REVISION = '2026-04-15';
const FORM_SELECTOR = 'form[data-klaviyo-list-id]';
const SUBMIT_TIMEOUT_MS = 1200;

let cachedCompanyId = null;

function getKlaviyoCompanyId() {
  if (cachedCompanyId) return cachedCompanyId;

  const scripts = Array.from(document.querySelectorAll('script[src*="klaviyo"]'));

  for (const script of scripts) {
    try {
      const src = script.getAttribute('src');
      if (!src || !src.includes('company_id=')) continue;

      const companyId = new URL(src, window.location.origin).searchParams.get('company_id');
      if (companyId) {
        cachedCompanyId = companyId;
        return companyId;
      }
    } catch (_) {
      // Ignore malformed URLs and continue probing other scripts.
    }
  }

  return '';
}

function buildPayload({ email, listId, source, tags, formType }) {
  return {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: source,
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: {
                signup_source: source,
                signup_form_type: formType,
                signup_page: window.location.pathname,
                shopify_signup_tags: tags,
              },
              subscriptions: {
                email: {
                  marketing: {
                    consent: 'SUBSCRIBED',
                  },
                },
              },
            },
          },
        },
      },
      relationships: {
        list: {
          data: {
            type: 'list',
            id: listId,
          },
        },
      },
    },
  };
}

function timeout(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function sendToKlaviyo({ companyId, payload }) {
  const response = await fetch(
    `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
      mode: 'cors',
      credentials: 'omit',
      keepalive: true,
    }
  );

  if (!response.ok) {
    throw new Error(`Klaviyo subscription failed with status ${response.status}`);
  }
}

function bindForm(form) {
  if (form.dataset.klaviyoSyncBound === 'true') return;
  form.dataset.klaviyoSyncBound = 'true';

  form.addEventListener('submit', (event) => {
    if (form.dataset.klaviyoSyncState === 'submitting') return;
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

    const listId = form.dataset.klaviyoListId?.trim();
    const source = form.dataset.klaviyoSource?.trim() || 'theme-form';
    const formType = form.dataset.klaviyoFormType?.trim() || 'shopify-customer-form';
    const emailInput = form.querySelector('input[name="contact[email]"], input[type="email"]');
    const email = emailInput?.value?.trim();
    const tags = form.querySelector('input[name="contact[tags]"]')?.value?.trim() || '';

    if (!listId || !email) return;

    const companyId = form.dataset.klaviyoCompanyId?.trim() || getKlaviyoCompanyId();
    if (!companyId) {
      console.warn('Klaviyo sync skipped: missing company_id on page.', { listId, source });
      return;
    }

    event.preventDefault();
    form.dataset.klaviyoSyncState = 'submitting';

    const submitButton = form.querySelector('[type="submit"]');
    const previousDisabled = Boolean(submitButton?.disabled);
    if (submitButton) submitButton.disabled = true;

    const payload = buildPayload({ email, listId, source, tags, formType });

    Promise.race([sendToKlaviyo({ companyId, payload }), timeout(SUBMIT_TIMEOUT_MS)])
      .catch((error) => {
        console.error('Klaviyo sync failed, falling back to Shopify-only submit.', error);
      })
      .finally(() => {
        if (submitButton) submitButton.disabled = previousDisabled;
        form.dataset.klaviyoSyncState = 'submitted';
        HTMLFormElement.prototype.submit.call(form);
      });
  });
}

function initKlaviyoFormSync(root = document) {
  root.querySelectorAll(FORM_SELECTOR).forEach(bindForm);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initKlaviyoFormSync());
} else {
  initKlaviyoFormSync();
}

document.addEventListener('shopify:section:load', (event) => {
  initKlaviyoFormSync(event.target);
});
